// /appv2/recreate/:id — Recreate runtime page.
//
// User picked a RecreateTemplate from the /create dashboard. Layout:
//
//   ┌──────────────────┐ ┌──────────────────┐
//   │                  │ │ Section 1        │
//   │  LIVE PREVIEW    │ │ Upload product   │ ← active by default
//   │                  │ │ [Continue]       │
//   │  Template image  │ ├──────────────────┤
//   │  by default →    │ │ Section 2        │
//   │  composed result │ │ Review/regen/dl  │ ← locked until gen
//   │  after gen       │ │                  │
//   │                  │ │                  │
//   └──────────────────┘ └──────────────────┘
//
// LEFT pane is the live preview — it always shows the most recent visual.
// At rest that's the template's preview PNG; after a successful generation
// the user's composed result replaces it in-place. While a run is in
// flight, a translucent overlay with spinner + status sits on top.
//
// RIGHT pane is a 2-section accordion (same Section component as /appv2's
// wizard so the visual language matches):
//
//   Section 1 — Upload your product
//     Active by default. StepUpload + Continue button. Clicking Continue
//     COLLAPSES section 1, OPENS section 2, AND fires the generation
//     pipeline in the background. "Edit" on the completed section row
//     pops the user back here.
//
//   Section 2 — Review & regenerate
//     Unlocks once Section 1 completes (after Continue). Shows status
//     while the pipeline runs, then action buttons when ready:
//       - Regenerate copy (cheap: re-runs /api/suggest-copy + recomposite
//         only; no /api/generate call, no extra credit charge)
//       - Regenerate everything (full re-run: 1 credit, fresh scene)
//       - Download PNG
//       - Save to gallery
//
// Generation pipeline (kicks off when Section 1's Continue fires):
//   1. /api/analyze-product → name + description + category
//   2. /api/suggest-copy → headline + tagline (skipped when template has
//      no role-tagged overlays)
//   3. /api/generate → composed product+scene image (1 credit reserved)
//   4. Canvas composite the AI image + role-substituted overlays
//
// Re-runs of just the copy reuse the cached base AI image from step 3, so
// the user can roll the dice on text without burning another generate call.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import Section from '@/pages/appv2/components/Section';
import StepUpload from '@/pages/app/components/StepUpload';
import {
  type AspectRatio,
  type Layer,
  type SuggestedFrame,
  type TextOverlay,
  aspectToRatio,
  renderLayersToCanvas,
} from '@/pages/app/lib/composeRender';
import {
  analyzeProduct,
  generateImage,
  listProducts,
  suggestCopy,
  wizardAiGenerate,
  type Quality,
  type SocialPostSlots,
  type UserProduct,
} from '@/lib/api';
import {
  PRODUCT_SHOT_AI_TEMPLATES,
  aiTemplateSamplePath,
  type AiTemplate,
} from '@/mocks/ai-templates';
import { savePost } from '@/lib/gallery';
import { useCredits } from '@/hooks/useCredits';
import { useBrand } from '@/hooks/useBrand';
import { productShotFormats, socialPlatforms } from '@/mocks/app-data';
import {
  getRecreateTemplate,
  RECREATE_CATEGORY_LABEL,
  type RecreateTemplate,
} from '@/mocks/recreate-templates';
import SocialPostExporter, {
  type SocialPostExporterHandle,
} from '@/components/feature/SocialPostExporter';
import {
  buildEffectiveSlots,
  composeSocialPost,
  fillSocialPostSlots,
} from '@/lib/socialPostPipeline';


// State machine for the underlying pipeline. Maps to the user-visible
// progress copy in Section 2 + the LEFT pane's loading overlay.
type RuntimeState =
  | 'idle'           // pre-Continue OR post-success awaiting next action
  | 'analyzing'      // /api/analyze-product in flight
  | 'copywriting'    // /api/suggest-copy in flight (skipped when no overlays)
  | 'generating'     // /api/generate in flight
  | 'compositing'    // canvas overlay render in progress (<100ms)
  | 'ready'          // composed image rendered, awaiting download / save
  | 'error';         // pipeline failed; show retry


// Overlays carry role + staticText at runtime (spread by applyPreset) even
// though TextOverlay's TS type doesn't declare them. Local extension keeps
// the substitution typesafe without touching composeRender.ts.
type OverlayWithRole = TextOverlay & {
  role?: 'headline' | 'tagline' | 'subtext' | 'cta' | 'static';
  staticText?: string;
};


// -----------------------------------------------------------------------------
// AI template adapter — /templates links the 10 Product Shot AI library
// tiles here with the raw AI template id (e.g. /appv2/recreate/editorial-luxe).
// Recreate ids own the :id namespace (checked first); AI library ids are
// the fallback. The synthetic RecreateTemplate keeps the whole page
// rendering unchanged — only runFullPipeline branches: the AI path calls
// /api/wizard/ai-generate with aiTemplateId (backend looks up the prompt
// in ai_templates.py and renders directly, concept LLM bypassed) instead
// of /api/generate with a scene templateId.
// -----------------------------------------------------------------------------
function aiTemplateAsRecreate(t: AiTemplate): RecreateTemplate {
  return {
    id: t.id,
    name: t.name,
    // 'product-shot' OR 'product-swap' (In Hand — model holding the
    // product). Drives the category badge + the route sent to the
    // wizard endpoint.
    category: t.route,
    // The AI library renders square — matches the 1:1 sample images.
    platformId: 'shot-1x1',
    // Never sent to /api/generate (the AI branch uses wizardAiGenerate);
    // kept distinct so gallery-save metadata identifies the source.
    templateId: `ai-template:${t.id}`,
    quality: 'low',
    // No text overlays — the prompt bakes the full creative. Disables
    // the copy-only regen path (hasOverlays=false) automatically.
    textOverlays: [],
    previewImage: aiTemplateSamplePath(t),
  };
}


export default function RecreatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { refresh: refreshCredits } = useCredits();
  const { activeBrand } = useBrand();

  // Resolve :id — Recreate catalog first (it owns the namespace), then
  // the AI template library. aiTemplateId is non-null ONLY when the id
  // resolved via the AI library; it flags the wizardAiGenerate branch in
  // runFullPipeline. Memoized so `template` keeps a stable identity
  // across renders (it sits in several useCallback dep arrays).
  const { template, aiTemplateId } = useMemo<{
    template: RecreateTemplate | undefined;
    aiTemplateId: string | null;
  }>(() => {
    const r = getRecreateTemplate(id);
    if (r) return { template: r, aiTemplateId: null };
    const t = PRODUCT_SHOT_AI_TEMPLATES.find((x) => x.id === id);
    return {
      template: t ? aiTemplateAsRecreate(t) : undefined,
      aiTemplateId: t?.id ?? null,
    };
  }, [id]);

  // Redirect-if-invalid runs in a mount effect (can't navigate during render).
  useEffect(() => {
    if (!template) {
      navigate('/create', { replace: true });
    }
  }, [template, navigate]);

  // --- Accordion state ---
  // 1 = Upload active (Section 2 locked)
  // 2 = Review active (Section 1 completed with filename summary)
  const [step, setStep] = useState<1 | 2>(1);

  // --- Upload state ---
  const [productImages, setProductImages] = useState<string[]>([]);
  const [productFiles, setProductFiles] = useState<File[]>([]);
  // Saved-products library (My Products) — fed into StepUpload's tabs
  // mode so the user can pick a saved product instead of re-uploading.
  // Null while the initial fetch is in flight (StepUpload defaults to
  // 'upload' tab in that case).
  const [savedProducts, setSavedProducts] = useState<UserProduct[] | null>(null);
  const [selectedSavedProductId, setSelectedSavedProductId] = useState<string | null>(null);

  // --- Runtime state ---
  const [runState, setRunState] = useState<RuntimeState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [composedDataUrl, setComposedDataUrl] = useState<string | null>(null);
  const [savedToGallery, setSavedToGallery] = useState(false);
  // ID of the gallery row created by Save / Schedule / Post. Captured
  // so Schedule + Post can navigate cleanly (Post links to the new
  // /creatives/:id detail page; Schedule lands on /schedule where the
  // user picks the post from the picker). Cleared whenever a fresh
  // pipeline run wipes the composed result.
  const [savedPostId, setSavedPostId] = useState<string | null>(null);
  // Which toolbar action is in-flight (drives the per-button spinner +
  // disables siblings while one is mid-save).
  const [toolbarBusy, setToolbarBusy] = useState<'schedule' | 'post' | null>(null);

  // Confirmation modal — fires when the user clicks "Try a different
  // template" while there's an unsaved result. Same pattern as the
  // /appv2 wizard's "Exit to Gallery" confirm.
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Cache the raw AI-composed base image so cheap copy regens don't have
  // to re-run /api/generate (which costs 1 credit). After the first full
  // run, "Regenerate copy" reuses this URL — just re-runs suggest-copy
  // and recomposites.
  const [baseImageUrl, setBaseImageUrl] = useState<string | null>(null);
  const [productInsights, setProductInsights] = useState<{
    name: string;
    description: string;
  } | null>(null);

  // Track in-flight runs so a fast double-click can't double-charge.
  const runInFlightRef = useRef(false);

  // -----------------------------------------------------------------
  // Social Post pipeline state
  // -----------------------------------------------------------------
  // Active only when template.category === 'single'. The Social Post
  // path doesn't follow the scene-place + overlay model the other
  // categories use — instead it composes the product on a flat color
  // (paste mode) OR merges it into a scene (scene-merge mode), then
  // renders the chosen JsonSocialPost / legacy design with AI-filled
  // copy slots, and exports the whole rendered post via html-to-image.
  //
  // The exporter component mounts whenever this state is non-null so
  // the runFullPipeline can imperatively call exportAsPng() on it
  // after a flushSync state update.
  const socialExporterRef = useRef<SocialPostExporterHandle>(null);
  const [socialDesignState, setSocialDesignState] = useState<{
    designId: string;
    slots: SocialPostSlots;
    productImageUrl: string;        // composed transparent product OR raw
    mergedSceneUrl: string | null;  // scene-merge result, if any
  } | null>(null);

  const isSocialPost = template?.category === 'single';


  // ---------- Helpers ----------

  // Derive the aspect ratio from the template's platformId.
  const platformObj =
    socialPlatforms.find((p) => p.id === template?.platformId) ??
    productShotFormats.find((p) => p.id === template?.platformId);
  const aspectRatio = (platformObj?.aspectRatio ?? '1:1') as AspectRatio;

  const handleUpload = useCallback((images: string[], files: File[]) => {
    setProductImages(images);
    setProductFiles(files);
    // Manual upload / URL scrape clear the saved-product selection
    // visual state. The library-pick handler re-sets it after this
    // runs (handleUpload-first ordering).
    setSelectedSavedProductId(null);
    // Don't reset composedDataUrl on a new upload — keeps the previous
    // result on the LEFT until Continue kicks off the new run. Stale-but-
    // useful is better than blank.
    setErrorMessage(null);
  }, []);

  // Fetch the saved-products library once on mount. StepUpload's tabs
  // mode defaults to the Library tab when this list is non-empty.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listProducts();
        if (!cancelled) setSavedProducts(list);
      } catch (err) {
        console.error('[Recreate] Failed to load saved products:', err);
        if (!cancelled) setSavedProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  // Library pick — fetch the public image URL into a File so it flows
  // into the same downstream pipeline as a manual upload. Same shape
  // as /appv2's handleSavedProductPick. Note: the recreate runtime
  // ALREADY runs analyze-product on Continue, so we don't need to
  // pre-populate name/description here (the analyze call will fill
  // them in anyway — cheap optimization deferred).
  const handleSavedProductPick = useCallback(async (product: UserProduct) => {
    if (!product.imageUrl) {
      throw new Error('This saved product has no image.');
    }
    const resp = await fetch(product.imageUrl);
    if (!resp.ok) {
      throw new Error(`Could not download the saved image (HTTP ${resp.status}).`);
    }
    const blob = await resp.blob();
    const filename = `saved-${product.id.slice(0, 8)}-${Date.now()}.jpg`;
    const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });
    // handleUpload clears selectedSavedProductId — call it FIRST, then
    // restore the selection so the tile keeps its checkmark.
    handleUpload([dataUrl], [file]);
    setSelectedSavedProductId(product.id);
  }, [handleUpload]);


  // Full pipeline run. Reusable for Section 1's Continue AND Section 2's
  // quality-picker regenerate. Optional qualityOverride lets the picker
  // re-run at a higher tier (Enhanced / Studio) without changing the
  // template's authored default.
  const runFullPipeline = useCallback(async (qualityOverride?: Quality) => {
    if (!template) return;
    if (productFiles.length === 0) return;
    if (runInFlightRef.current) return;
    runInFlightRef.current = true;

    setErrorMessage(null);
    setSavedToGallery(false);
    setSavedPostId(null);

    const productFile = productFiles[0];
    const overlays = (template.textOverlays ?? []) as OverlayWithRole[];
    const effectiveQuality = (qualityOverride ?? template.quality) as Quality;

    try {
      // 1. Analyze product (shared first step for every category).
      setRunState('analyzing');
      const analysis = await analyzeProduct(productFile);
      setProductInsights({
        name: analysis.name,
        description: analysis.description,
      });

      // ============================================================
      // AI TEMPLATE PATH (Product Shot AI library)
      // ============================================================
      // Prompt-driven render via /api/wizard/ai-generate — the backend
      // looks up the template's prompt (backend/ai_templates.py) and
      // uses it verbatim as visual_direction, bypassing the concept
      // LLM. No text overlays — the returned frame IS the final
      // creative, so it flows straight into composedDataUrl. Same
      // credit math as the scene path (quality multiplier × 1 frame).
      if (aiTemplateId) {
        setRunState('generating');
        const result = await wizardAiGenerate({
          product: productFile,
          // In Hand templates ride the product-swap route (feature-key
          // routing + logging); everything else is product-shot. The
          // render itself is identical — the template prompt is the
          // visual_direction either way.
          route: template.category === 'product-swap' ? 'product-swap' : 'product-shot',
          aspectRatio,
          quality: effectiveQuality,
          productName: analysis.name,
          productDescription: analysis.description,
          aiTemplateId,
        });
        setComposedDataUrl(`data:image/png;base64,${result.frames[0]}`);
        setRunState('ready');
        refreshCredits();
        return;
      }

      // ============================================================
      // SOCIAL POST PATH (category === 'single')
      // ============================================================
      // Scene-place + overlays would just generate a single big image
      // here — wrong for Social Post, where the template is a layered
      // design that slots the product INTO a fixed layout. Instead we:
      //   1. Fill slots via suggestSocialCopy (12-slot format)
      //   2. Compose product (paste OR scene-merge) via socialPostPipeline
      //   3. Mount the design via SocialPostExporter and capture as PNG
      // The captured PNG flows into the existing composedDataUrl pipe so
      // Section 2 (download / save) just works.
      if (template.category === 'single' && template.socialDesignId) {
        const designId = template.socialDesignId;

        setRunState('copywriting');
        const rawSlots = await fillSocialPostSlots({
          productFile,
          productName: analysis.name,
          productDescription: analysis.description,
        });
        const slots = buildEffectiveSlots(rawSlots, analysis.name);

        setRunState('generating');
        const compose = await composeSocialPost({
          productFile,
          designId,
          productName: analysis.name,
          productDescription: analysis.description,
          hasHumanModel: analysis.hasHumanModel ?? false,
          onCreditsChanged: refreshCredits,
        });

        // Push the design's render-time props into state. flushSync so
        // the exporter has the new props committed BEFORE we call
        // exportAsPng — without it React may batch the update and the
        // capture would read stale props (placeholder slots / no
        // product image).
        setRunState('compositing');
        flushSync(() => {
          setSocialDesignState({
            designId,
            slots,
            productImageUrl: compose.composedProductUrl ?? '',
            mergedSceneUrl: compose.mergedSceneUrl,
          });
        });

        // Wait one frame so the new design tree paints + lays out
        // before html-to-image walks it. exportAsPng also awaits
        // document.fonts.ready internally.
        await new Promise<void>((r) => requestAnimationFrame(() => r()));

        if (!socialExporterRef.current) {
          throw new Error('Social Post exporter failed to mount');
        }
        // Export at the platform's native width. platformObj.dimensions
        // is the canonical source; fall back to 1080 if missing.
        const exportWidth = parsePlatformWidth(platformObj?.dimensions) ?? 1080;
        const dataUrl = await socialExporterRef.current.exportAsPng(exportWidth);

        setComposedDataUrl(dataUrl);
        setRunState('ready');
        return;
      }

      // ============================================================
      // DEFAULT PATH (product-shot / fashion / carousel / product-swap)
      // ============================================================

      // 2. Suggest copy (only when template has replaceable slots).
      let suggestedFrame: SuggestedFrame | undefined;
      const hasReplaceableSlots = overlays.some(
        (o) => o.role === 'headline' || o.role === 'tagline' || o.role === 'subtext',
      );
      if (hasReplaceableSlots) {
        setRunState('copywriting');
        const copyResult = await suggestCopy({
          productFile,
          productName: analysis.name,
          productDescription: analysis.description,
          frameCount: 1,
        });
        suggestedFrame = copyResult.frames[0];
      }

      // 3. Generate the composed image. Quality is the override when set,
      // otherwise the template's authored default.
      setRunState('generating');
      const result = await generateImage({
        productFile,
        templateId: template.templateId,
        frameNumber: 1,
        aspectRatio,
        productName: analysis.name,
        productDescription: analysis.description,
        quality: effectiveQuality,
        provider: template.provider ?? 'openai',
        garmentType: template.garmentType,
        bgHex: template.bgHex,
      });
      setBaseImageUrl(result.imageUrl);

      // 4. Composite.
      setRunState('compositing');
      const substituted: TextOverlay[] = overlays.map((o) =>
        substituteOverlayText(o, suggestedFrame),
      );
      const composed = await compositeWithOverlays(
        result.imageUrl,
        substituted,
        aspectRatio,
      );
      setComposedDataUrl(composed);
      setRunState('ready');
      refreshCredits();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Unexpected error during generation';
      setErrorMessage(msg);
      setRunState('error');
    } finally {
      runInFlightRef.current = false;
    }
  }, [template, aiTemplateId, productFiles, aspectRatio, platformObj, refreshCredits]);


  // Cheap copy-only regeneration. Re-runs suggest-copy + recomposites
  // using the CACHED base image — no /api/generate call, no credit charge.
  // Only available after a successful full run (so we have baseImageUrl
  // + productInsights cached).
  const runCopyOnlyRegeneration = useCallback(async () => {
    if (!template) return;
    if (!baseImageUrl || !productInsights) return;
    if (productFiles.length === 0) return;
    if (runInFlightRef.current) return;
    runInFlightRef.current = true;

    setErrorMessage(null);
    setSavedToGallery(false);
    setSavedPostId(null);

    const productFile = productFiles[0];
    const overlays = (template.textOverlays ?? []) as OverlayWithRole[];
    const hasReplaceableSlots = overlays.some(
      (o) => o.role === 'headline' || o.role === 'tagline' || o.role === 'subtext',
    );

    // No-op for templates with no replaceable slots — there's nothing to
    // regenerate. Surface this in the button's disabled state instead of
    // here, but guard defensively too.
    if (!hasReplaceableSlots) {
      runInFlightRef.current = false;
      return;
    }

    try {
      setRunState('copywriting');
      const copyResult = await suggestCopy({
        productFile,
        productName: productInsights.name,
        productDescription: productInsights.description,
        frameCount: 1,
      });
      const suggestedFrame = copyResult.frames[0];

      setRunState('compositing');
      const substituted: TextOverlay[] = overlays.map((o) =>
        substituteOverlayText(o, suggestedFrame),
      );
      const composed = await compositeWithOverlays(
        baseImageUrl,
        substituted,
        aspectRatio,
      );
      setComposedDataUrl(composed);
      setRunState('ready');
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Unexpected error during copy regeneration';
      setErrorMessage(msg);
      setRunState('error');
    } finally {
      runInFlightRef.current = false;
    }
  }, [template, baseImageUrl, productInsights, productFiles, aspectRatio]);


  // Section 1 → 2 transition. Collapses the upload section + opens the
  // result section + kicks off the pipeline so the result is ready (or
  // mid-flight with progress visible) by the time the user looks.
  const handleContinueFromUpload = useCallback(() => {
    setStep(2);
    // Fire-and-forget — runFullPipeline owns its own error handling and
    // run-state transitions. Awaiting here would just defer until done.
    void runFullPipeline();
  }, [runFullPipeline]);


  // Section 1 Edit pill — go back to upload step. Leaves the existing
  // result/state alone (so if the user just wanted to glance back, they
  // can hit Continue again without re-running).
  const handleEditUpload = useCallback(() => {
    setStep(1);
  }, []);


  const handleDownload = useCallback(() => {
    if (!composedDataUrl || !template) return;
    const a = document.createElement('a');
    a.href = composedDataUrl;
    a.download = `${template.id}-${Date.now()}.png`;
    a.click();
  }, [composedDataUrl, template]);


  // "Try a different template" — if there's an unsaved result, prompt
  // for confirmation since navigating away wipes the composed image.
  // "Saved to gallery" counts as saved progress (the post is preserved),
  // so we still prompt — the user may have iterated AFTER saving and
  // not realized the gallery copy is stale.
  const handleTryDifferent = useCallback(() => {
    if (composedDataUrl) {
      setShowLeaveConfirm(true);
    } else {
      navigate('/create');
    }
  }, [composedDataUrl, navigate]);

  const confirmLeave = useCallback(() => {
    setShowLeaveConfirm(false);
    navigate('/create');
  }, [navigate]);

  const cancelLeave = useCallback(() => {
    setShowLeaveConfirm(false);
  }, []);


  // Save-once. Returns the gallery row id so callers (Save / Schedule /
  // Post) can navigate or chain off it. Idempotent: if savedPostId is
  // already set we return that immediately — no duplicate uploads.
  const handleSaveToGallery = useCallback(async (): Promise<string | null> => {
    if (!composedDataUrl || !template) return null;
    if (savedPostId) return savedPostId;
    try {
      const blob = await dataUrlToBlob(composedDataUrl);
      const headlineLayer = (template.textOverlays as OverlayWithRole[] | undefined)?.find(
        (l) => l.role === 'headline',
      );
      const taglineLayer = (template.textOverlays as OverlayWithRole[] | undefined)?.find(
        (l) => l.role === 'tagline',
      );
      const newId = await savePost({
        blob,
        templateId: template.templateId,
        aspectRatio,
        headline: headlineLayer?.text ?? null,
        tagline: taglineLayer?.text ?? null,
        title: template.name,
      });
      setSavedToGallery(true);
      setSavedPostId(newId);
      return newId;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save to gallery failed';
      setErrorMessage(msg);
      return null;
    }
  }, [composedDataUrl, template, aspectRatio, savedPostId]);

  // Toolbar: Schedule — implicitly saves to gallery (so the post exists),
  // then bounces to the calendar surface where the user picks a date.
  // Future polish: deep-link prefill via ?postId=… so the schedule modal
  // pre-selects this post. For v1 the user picks from the modal grid.
  const handleSchedule = useCallback(async () => {
    if (!composedDataUrl) return;
    if (toolbarBusy) return;
    setToolbarBusy('schedule');
    setErrorMessage(null);
    const id = await handleSaveToGallery();
    if (!id) {
      setToolbarBusy(null);
      return;
    }
    navigate('/schedule');
  }, [composedDataUrl, toolbarBusy, handleSaveToGallery, navigate]);

  // Toolbar: Post — implicitly saves, then jumps into the detail page
  // where the user picks Facebook / Instagram + edits the caption.
  // All publishing UX lives on /creatives/:id (single source of truth).
  const handlePost = useCallback(async () => {
    if (!composedDataUrl) return;
    if (toolbarBusy) return;
    setToolbarBusy('post');
    setErrorMessage(null);
    const id = await handleSaveToGallery();
    if (!id) {
      setToolbarBusy(null);
      return;
    }
    navigate(`/creatives/${id}`);
  }, [composedDataUrl, toolbarBusy, handleSaveToGallery, navigate]);


  // Guard: render nothing while the redirect effect fires.
  if (!template) {
    return null;
  }


  // ---------- Render ----------

  // Left-pane image source: result if we have one, otherwise the template
  // preview.
  const leftImageSrc = composedDataUrl ?? template.previewImage;
  const leftIsResult = !!composedDataUrl;
  const isWorking_ = isWorking(runState);
  const hasOverlays = (template.textOverlays?.length ?? 0) > 0;
  const uploadSummary =
    productFiles[0]?.name ?? '(no file uploaded yet — click Edit to add one)';

  return (
    <AppLayoutV2>
      {/* Hidden Social Post exporter — only mounts for category='single'
          templates AND only after the compose pipeline has resolved its
          state. The exporter renders the JsonSocialPost (or legacy)
          design at 1080×1080 inside an off-flow div; runFullPipeline
          calls its imperative exportAsPng() to capture the final PNG. */}
      {isSocialPost && socialDesignState && (
        <SocialPostExporter
          ref={socialExporterRef}
          designId={socialDesignState.designId}
          slots={socialDesignState.slots}
          productImageUrl={socialDesignState.productImageUrl}
          mergedSceneUrl={socialDesignState.mergedSceneUrl}
        />
      )}

      <div className="max-w-[1500px] mx-auto px-6 py-8">

        {/* Header — back to dashboard + template name. */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate('/create')}
              className="inline-flex items-center gap-1.5 text-sm text-dark-muted hover:text-dark transition-colors cursor-pointer mb-2"
            >
              <i className="ri-arrow-left-line" />
              Back to templates
            </button>
            <h1 className="font-display font-bold text-dark text-2xl">
              {template.name}
            </h1>
            <p className="text-dark-muted text-sm mt-1">
              <span className="inline-block px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-bold uppercase tracking-wide mr-2">
                {RECREATE_CATEGORY_LABEL[template.category]}
              </span>
              Drop in your product and we'll do the rest.
            </p>
          </div>
        </div>

        {/* Two-pane layout: LEFT = live preview (sticky on wide so it stays
            in view as the right-side accordion scrolls). RIGHT = accordion. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {/* ---- LEFT: Live preview ---- */}
          {/* No sticky/self-start here — both columns rely on the grid's
              default `stretch` alignment so the right column knows what
              height to fill (= LEFT's aspect-square + footer). */}
          <div>
            <div className="bg-white rounded-2xl border-2 border-surface-dark/30 overflow-hidden h-full flex flex-col">
              <div className="aspect-square bg-surface relative">
                <img
                  src={leftImageSrc}
                  alt={leftIsResult ? 'Your composed post' : `${template.name} template preview`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                {isWorking_ && (
                  <div className="absolute inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center text-white">
                    <div className="flex flex-col items-center gap-3">
                      <i className="ri-loader-4-line text-4xl animate-spin" />
                      <span className="text-sm font-semibold">
                        {runState === 'analyzing'   && 'Analyzing product…'}
                        {runState === 'copywriting' && 'Writing copy for your product…'}
                        {runState === 'generating'  && 'Composing scene…'}
                        {runState === 'compositing' && 'Rendering text…'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-surface-dark/20">
                <p className="text-xs text-dark-muted">
                  {leftIsResult
                    ? (aiTemplateId
                      ? 'Your product, rendered in this template\'s style.'
                      : 'Your product, swapped into the same scene and text styling as the template.')
                    : (aiTemplateId
                      ? 'Upload your product on the right and we\'ll render it in this template\'s style.'
                      : 'Upload your product on the right and we\'ll swap it into this scene with copy tailored to your product.')}
                </p>
              </div>
            </div>
          </div>

          {/* ---- RIGHT: Accordion (Section 1 + Section 2) ----
              flex-col so the children stack and Section 2 can grow to
              fill the column height (which matches the LEFT image's
              height via the grid's default stretch alignment). */}
          <div className="flex flex-col min-h-0">

            {/* SECTION 1 — Upload (natural height) */}
            <Section
              step={1}
              title="Upload your product"
              icon="ri-upload-cloud-line"
              description="Drop in an image or paste a product URL."
              state={step === 1 ? 'active' : 'completed'}
              summary={uploadSummary}
              onEdit={handleEditUpload}
              cta={
                <button
                  onClick={() => handleContinueFromUpload()}
                  disabled={productFiles.length === 0 || isWorking_}
                  className={`
                    inline-flex items-center justify-center gap-2
                    px-7 py-3 rounded-full text-sm font-bold transition-all
                    cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed
                    ${productFiles.length > 0 && !isWorking_
                      ? 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25'
                      : 'bg-surface-dark/30 text-dark-muted cursor-not-allowed'}
                  `}
                >
                  <i className="ri-magic-line text-base" />
                  Generate
                  <i className="ri-arrow-right-line text-base" />
                </button>
              }
            >
              <StepUpload
                uploadedImages={productImages}
                maxImages={1}
                onUpload={handleUpload}
                platformId={template.platformId}
                // Saved-products library + tabs mode. Right pane is too
                // narrow here for the side-by-side library layout used
                // in the wizard's Upload step — tabs let us fit the
                // same picker in the limited horizontal space.
                savedProducts={savedProducts ?? []}
                savedProductsLoading={savedProducts === null}
                onSavedProductSelect={handleSavedProductPick}
                selectedSavedProductId={selectedSavedProductId}
                tabsMode
                compact
                compactPreview
              />
            </Section>

            {/* SECTION 2 — Review & regenerate (fills remaining height) */}
            <Section
              step={2}
              title="Review & download"
              icon="ri-magic-line"
              description={
                runState === 'ready'
                  ? 'Looking good? Download below — or regenerate to try again.'
                  : runState === 'error'
                  ? 'Something went wrong. Retry from below.'
                  : 'Generating your post — hang on for a few seconds…'
              }
              state={step === 2 ? 'active' : 'locked'}
              fill
            >
              <Section2Content
                runState={runState}
                hasOverlays={hasOverlays}
                templateQuality={template.quality as Quality}
                errorMessage={errorMessage}
                savedToGallery={savedToGallery}
                isWorking={isWorking_}
                toolbarBusy={toolbarBusy}
                onRegenerateCopy={runCopyOnlyRegeneration}
                onRegenerateAt={runFullPipeline}
                onDownload={handleDownload}
                onSaveToGallery={handleSaveToGallery}
                onSchedule={handleSchedule}
                onPost={handlePost}
                onTryDifferent={handleTryDifferent}
              />
            </Section>

          </div>
        </div>
      </div>

      {/* "Try a different template" confirmation modal. Fires when the
          user has a composed result on the LEFT (which they'd lose by
          navigating away). Reuses the same modal treatment as /appv2's
          gallery-exit confirm. */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl border border-surface-dark/40 p-6 max-w-md w-full shadow-xl">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-amber-500 text-xl" />
            </div>
            <h3 className="font-display font-bold text-dark text-lg mb-2">
              Leave this template?
            </h3>
            <p className="text-dark-muted text-sm mb-2">
              You'll go back to the template gallery to pick a different one.
            </p>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-6">
              <p className="text-amber-800 text-sm font-medium flex items-start gap-2">
                <i className="ri-alert-line text-amber-500 mt-0.5 flex-shrink-0" />
                Your current result will be lost. Download or save to gallery
                first if you want to keep it.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={cancelLeave}
                className="flex-1 px-4 py-2.5 rounded-full border border-dark/10 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
              >
                Stay &amp; keep result
              </button>
              <button
                onClick={confirmLeave}
                className="flex-1 px-4 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
              >
                Yes, leave
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayoutV2>
  );
}


// =============================================================================
// Section 2 body — broken out so the parent's render stays readable.
// Three branches:
//   - In flight: status text + spinner (LEFT pane carries the full visual)
//   - Error: red panel + retry button
//   - Ready: standardized action layout
//       primary row:   Download | Save to gallery
//       secondary row: "Regenerate text with AI" (gradient, matches StepText)
//                    + Regenerate (popover with Regular/Enhanced/Studio)
//       link:          Try a different template (bottom-anchored)
// =============================================================================

interface Section2ContentProps {
  runState: RuntimeState;
  hasOverlays: boolean;
  /** Authored quality of the template — surfaced as "Current" in the
   *  regenerate popover so the user can re-roll at the same tier OR
   *  upgrade to Enhanced / Studio. */
  templateQuality: Quality;
  errorMessage: string | null;
  savedToGallery: boolean;
  isWorking: boolean;
  onRegenerateCopy: () => void | Promise<void>;
  /** Quality-targeted regenerate (Regular = 1, Enhanced = 2, Studio = 4
   *  credits). Re-runs the full pipeline at the given tier. */
  onRegenerateAt: (quality: Quality) => void | Promise<void>;
  onDownload: () => void;
  onSaveToGallery: () => Promise<string | null>;
  /** Toolbar action — save (idempotent) + navigate to /schedule. */
  onSchedule: () => void | Promise<void>;
  /** Toolbar action — save (idempotent) + navigate to /creatives/:id. */
  onPost: () => void | Promise<void>;
  /** Per-button spinner driver. */
  toolbarBusy: 'schedule' | 'post' | null;
  onTryDifferent: () => void;
}

function Section2Content({
  runState,
  hasOverlays,
  templateQuality,
  errorMessage,
  savedToGallery,
  isWorking,
  toolbarBusy,
  onRegenerateCopy,
  onRegenerateAt,
  onDownload,
  onSaveToGallery,
  onSchedule,
  onPost,
  onTryDifferent,
}: Section2ContentProps) {

  // --- In flight: thin status line. The LEFT pane carries the full
  // visual progress; this is just a calm inline echo. ---
  if (isWorking) {
    return (
      <div className="flex items-center gap-3 py-2 text-dark-muted text-sm">
        <i className="ri-loader-4-line text-base animate-spin" />
        {runState === 'analyzing'   && 'Analyzing your product…'}
        {runState === 'copywriting' && 'Writing copy tailored to your product…'}
        {runState === 'generating'  && 'Composing the scene with your product…'}
        {runState === 'compositing' && 'Rendering the text…'}
      </div>
    );
  }

  // --- Error: red panel + retry. ---
  if (runState === 'error') {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <strong>Generation failed:</strong> {errorMessage ?? 'Unknown error'}
        </div>
        <button
          onClick={() => onRegenerateAt(templateQuality)}
          className="
            w-full inline-flex items-center justify-center gap-2
            px-6 py-3 rounded-full text-sm font-bold transition-all
            cursor-pointer bg-primary hover:bg-primary-hover text-white
            shadow-md shadow-primary/25
          "
        >
          <i className="ri-refresh-line text-base" />
          Try again
        </button>
      </div>
    );
  }

  // --- Ready: standardized actions arranged top-to-bottom:
  //     TOP: "Not quite right" tray (regenerate options)
  //     SPACER: flex-1 (only when in fill mode)
  //     BOTTOM: Download / Save primary actions + Try a different link
  //
  // Layout choice driven by the user mental model: "what do I want to
  // do next?" → first see the iteration tools (top), then the commit
  // actions (bottom, where the eye naturally lands as the final move). ---
  return (
    <div className="flex flex-col h-full">

      {/* TOP — "Not quite right?" tray. Sits up here because the
          iteration loop is the most-used action AFTER first generation:
          look at result on the LEFT, decide it's not quite right, hit
          one of these regen buttons. */}
      <div className="space-y-4">

        <div className="rounded-xl border border-dashed border-surface-dark/40 p-3 bg-white/40 space-y-2">
          <p className="text-xs text-dark-muted font-semibold">
            Not quite right?
          </p>

          {/* Regenerate text with AI — gradient button matching StepText. */}
          <button
            onClick={onRegenerateCopy}
            disabled={!hasOverlays || runState !== 'ready'}
            title={
              !hasOverlays
                ? 'This template has no editable text.'
                : 'Roll the dice on new headline + tagline. Free (no credit charge).'
            }
            className="
              group relative w-full inline-flex items-center justify-center gap-1.5
              px-3 py-2 rounded-lg overflow-hidden
              bg-gradient-to-r from-primary via-fuchsia-500 to-accent
              text-white text-xs font-bold tracking-wide
              shadow-md shadow-primary/25
              hover:shadow-lg hover:shadow-primary/40 hover:-translate-y-px
              active:translate-y-0
              transition-all duration-200 cursor-pointer
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-md
            "
          >
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700" />
            <i className="ri-sparkling-2-line text-sm relative" />
            <span className="relative">Regenerate text with AI</span>
          </button>

          {/* Quality picker — popover with Regular/Enhanced/Studio. */}
          <RegenerateQualityPicker
            currentQuality={templateQuality}
            disabled={runState !== 'ready'}
            onPick={onRegenerateAt}
          />
        </div>

        {/* Inline non-fatal error (e.g. save-to-gallery failed). */}
        {errorMessage && runState === 'ready' && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            {errorMessage}
          </div>
        )}

      </div>

      {/* SPACER — pushes the primary action group to the bottom when
          Section 2 is in `fill` mode. Without fill, this is just 0px. */}
      <div className="flex-1 min-h-0" aria-hidden="true" />

      {/* BOTTOM — primary commit actions + escape link. Anchored at the
          bottom of the section so the user's eye naturally lands here
          after surveying the result + iteration options above. */}
      <div className="space-y-3">

        {/* Toolbar — same visual language as the gallery tile hover
            icons (circular white pills with brand-color hover). Three
            primary actions: Download (instant), Schedule (saves +
            bounces to /schedule), Post (saves + opens /creatives/:id
            for FB/IG publishing). Schedule + Post both implicitly save
            so the post exists in My Creatives — that's where the
            scheduler + publish endpoints expect it. */}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={onDownload}
            disabled={runState !== 'ready'}
            title="Download PNG"
            aria-label="Download PNG"
            className="w-12 h-12 rounded-full bg-white border border-surface-dark/50 hover:bg-primary hover:text-white hover:border-primary text-dark shadow-md flex items-center justify-center cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="ri-download-line text-xl" />
          </button>
          <button
            onClick={onSchedule}
            disabled={runState !== 'ready' || toolbarBusy !== null}
            title="Save and schedule"
            aria-label="Save and schedule"
            className="w-12 h-12 rounded-full bg-white border border-surface-dark/50 hover:bg-primary hover:text-white hover:border-primary text-dark shadow-md flex items-center justify-center cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {toolbarBusy === 'schedule' ? (
              <i className="ri-loader-4-line text-xl animate-spin" />
            ) : (
              <i className="ri-calendar-line text-xl" />
            )}
          </button>
          <button
            onClick={onPost}
            disabled={runState !== 'ready' || toolbarBusy !== null}
            title="Save and post to social"
            aria-label="Save and post to social"
            className="w-12 h-12 rounded-full bg-white border border-surface-dark/50 hover:bg-primary hover:text-white hover:border-primary text-dark shadow-md flex items-center justify-center cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {toolbarBusy === 'post' ? (
              <i className="ri-loader-4-line text-xl animate-spin" />
            ) : (
              <i className="ri-send-plane-fill text-xl" />
            )}
          </button>
        </div>

        {/* Secondary save action — silent if user just wants to file
            the result in My Creatives without scheduling or posting.
            Hidden after a successful save (Schedule / Post also flip
            this; same one-click commit, different next-step). */}
        {!savedToGallery ? (
          <button
            onClick={onSaveToGallery}
            disabled={runState !== 'ready' || toolbarBusy !== null}
            className="w-full text-xs text-dark-muted hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-gallery-line mr-1.5" />
            Just save to My Creatives
          </button>
        ) : (
          <p className="text-xs text-emerald-700 text-center flex items-center justify-center gap-1.5">
            <i className="ri-check-line" />
            Saved to My Creatives
          </p>
        )}

        <div className="text-center pt-2 border-t border-surface-dark/20">
          <button
            onClick={onTryDifferent}
            className="text-sm text-primary hover:underline cursor-pointer font-medium"
          >
            Try a different template →
          </button>
        </div>

      </div>
    </div>
  );
}


// =============================================================================
// RegenerateQualityPicker — button + popover. Click opens a panel above
// the button with 3 quality options matching StepQuality's vocabulary:
//   Regular  (low)    — 1 credit
//   Enhanced (medium) — 2 credits
//   Studio   (high)   — 4 credits
// The template's authored quality gets a "Current" badge so the user
// sees what they were just generating at, and can either re-roll at
// the same tier or upgrade. Picking an option closes the popover and
// fires the regenerate-at-quality handler.
// =============================================================================

interface QualityOption {
  quality: Quality;
  label: string;
  credits: number;
  icon: string;
}

const QUALITY_OPTIONS: QualityOption[] = [
  { quality: 'low',    label: 'Regular',  credits: 1, icon: 'ri-flashlight-line' },
  { quality: 'medium', label: 'Enhanced', credits: 2, icon: 'ri-star-line' },
  { quality: 'high',   label: 'Studio',   credits: 4, icon: 'ri-vip-crown-line' },
];

function RegenerateQualityPicker({
  currentQuality,
  disabled,
  onPick,
}: {
  currentQuality: Quality;
  disabled: boolean;
  onPick: (quality: Quality) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Popover position computed from the trigger button's viewport rect.
  // Recomputed on open + on scroll/resize while open, so the panel
  // tracks the button if the page scrolls. Position is viewport-relative
  // because we use `position: fixed` on the portal'd panel.
  const [position, setPosition] = useState<{ left: number; top: number; width: number } | null>(null);

  // Compute popover position. Anchors the panel's BOTTOM-RIGHT corner
  // to the trigger button's TOP-RIGHT corner (with 8px gap), so it
  // opens above-and-flush-right with the button. Width matches the
  // button (capped at 256px so on very narrow mobile views the panel
  // still looks like a menu, not a full-width sheet).
  const computePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const PANEL_W = Math.min(rect.width, 256);
    const GAP = 8;
    setPosition({
      // `right edge` of panel aligns with `right edge` of trigger.
      left: rect.right - PANEL_W,
      // `bottom edge` of panel sits GAP above `top edge` of trigger —
      // achieved via top + translateY(-100%) in the style block below.
      top: rect.top - GAP,
      width: PANEL_W,
    });
  }, []);

  // Close on outside-click + Escape. Also recompute position on scroll/
  // resize so the panel tracks the button across viewport changes.
  useEffect(() => {
    if (!open) return;
    computePosition();
    const onMouseDown = (e: MouseEvent) => {
      // Don't close when clicking the trigger button (its onClick toggles
      // open already; closing here would double-fire).
      if (buttonRef.current?.contains(e.target as Node)) return;
      // Don't close when clicking inside the panel itself — the panel's
      // own option onClicks handle their own close.
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-quality-picker-panel]')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollOrResize = () => computePosition();
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, computePosition]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="
          w-full inline-flex items-center justify-center gap-1.5
          px-3 py-2 rounded-lg text-xs font-semibold transition-all
          cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed
          border border-surface-dark/40 text-dark bg-white
          hover:border-primary/40 hover:text-primary
        "
      >
        <i className="ri-refresh-line text-sm" />
        Regenerate
        <i className={`text-sm ${open ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'}`} />
      </button>

      {/* Popover rendered via portal at document.body so it escapes the
          parent Section's overflow-hidden clipping. Viewport-positioned
          via getBoundingClientRect → position: fixed. */}
      {open && position && createPortal(
        <div
          data-quality-picker-panel
          className="
            fixed z-[100]
            rounded-xl bg-white shadow-2xl border border-surface-dark/30
            p-2 space-y-1
          "
          style={{
            left: position.left,
            top: position.top,
            width: position.width,
            transform: 'translateY(-100%)',
          }}
          role="menu"
        >
          <p className="px-3 py-1 text-[10px] font-bold text-dark-muted uppercase tracking-wider">
            Pick a quality to regenerate
          </p>
          {QUALITY_OPTIONS.map((opt) => {
            const isCurrent = opt.quality === currentQuality;
            return (
              <button
                key={opt.quality}
                onClick={() => {
                  setOpen(false);
                  void onPick(opt.quality);
                }}
                className={`
                  w-full flex items-center gap-2.5 px-2 py-2 rounded-lg
                  text-left transition-colors cursor-pointer
                  ${isCurrent ? 'bg-primary/5' : 'hover:bg-surface'}
                `}
                role="menuitem"
              >
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                  ${isCurrent ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}
                `}>
                  <i className={`${opt.icon} text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-dark leading-tight">
                    {opt.label}
                  </p>
                  {isCurrent && (
                    <p className="text-[10px] text-primary font-semibold leading-tight">
                      Current
                    </p>
                  )}
                </div>
                <span className="inline-flex items-center gap-1 text-xs text-primary font-bold flex-shrink-0">
                  <i className="ri-copper-coin-line text-sm" />
                  {opt.credits}
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}


// ---------- Helpers ----------

function isWorking(state: RuntimeState): boolean {
  return (
    state === 'analyzing' ||
    state === 'copywriting' ||
    state === 'generating' ||
    state === 'compositing'
  );
}


/**
 * Parse a platform's dimensions string ("1080 × 1080", "1200 × 1200",
 * "1080x1350", etc.) into its WIDTH in pixels. Falls back to null if
 * the string isn't parseable so the caller can default to 1080.
 *
 * The platform table is hand-authored, so format variation is
 * minimal — we accept any " × " or "x" separator and the first
 * digit-run is treated as width.
 */
function parsePlatformWidth(dim: string | undefined): number | null {
  if (!dim) return null;
  const m = dim.match(/(\d+)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}


/**
 * Substitute the user-product-tailored copy into one text overlay, based
 * on its `role`. See applyPreset's resolveLayerText in composeRender.ts
 * for the matching rules.
 */
function substituteOverlayText(
  overlay: OverlayWithRole,
  frame: SuggestedFrame | undefined,
): TextOverlay {
  if (!frame) return overlay;
  if (overlay.role === 'static') return overlay;
  if (overlay.role === 'cta' && overlay.staticText) return overlay;
  if (overlay.role === 'headline' && frame.headline) {
    return { ...overlay, text: frame.headline };
  }
  if (overlay.role === 'tagline' && frame.tagline) {
    return { ...overlay, text: frame.tagline };
  }
  if (overlay.role === 'subtext' && frame.subtext) {
    return { ...overlay, text: frame.subtext };
  }
  return overlay;
}


/**
 * Composite a generated product image with the template's text overlays.
 * Returns a data URL (PNG) ready to download or upload to Storage.
 */
async function compositeWithOverlays(
  baseImageUrl: string,
  overlays: Layer[],
  aspectRatio: AspectRatio,
): Promise<string> {
  await document.fonts.ready;
  const baseImg = await loadImage(baseImageUrl);
  const W = 1080;
  const H = Math.round(W / aspectToRatio(aspectRatio));
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(baseImg, 0, 0, W, H);
  if (overlays.length > 0) {
    renderLayersToCanvas(ctx, W, H, overlays);
  }
  return canvas.toDataURL('image/png');
}


function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}


async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const resp = await fetch(dataUrl);
  return resp.blob();
}


export type { RecreateTemplate };
